import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Search, RefreshCw, Download } from 'lucide-react';
import { adminDb } from '../lib/adminDb';

interface AuditEntry {
  id: string;
  admin_email: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  old_value: any;
  new_value: any;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  'feature_flag.toggle': 'Feature Flag umgeschaltet',
  'user.premium_toggle': 'Premium Status geändert',
  'question.update': 'Frage aktualisiert',
  'question.delete': 'Frage gelöscht',
  'question.create': 'Frage erstellt',
  'image.generate': 'Bild generiert',
};

const ACTION_COLORS: Record<string, string> = {
  'feature_flag.toggle': 'badge-info',
  'user.premium_toggle': 'badge-success',
  'question.update': 'badge-warning',
  'question.delete': 'badge-danger',
  'question.create': 'badge-success',
  'image.generate': 'badge-info',
};

export const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAuditLog();
  }, []);

  const loadAuditLog = async () => {
    setLoading(true);
    try {
      const { data, error } = await adminDb
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setEntries(data || []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = React.useMemo(() => {
    let result = [...entries];
    if (filterAction) result = result.filter(e => e.action === filterAction);
    if (filterResource) result = result.filter(e => e.resource_type === filterResource);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.admin_email.toLowerCase().includes(term) ||
        e.action.toLowerCase().includes(term) ||
        e.resource_id?.toLowerCase().includes(term)
      );
    }
    return result;
  }, [entries, filterAction, filterResource, searchTerm]);

  const uniqueActions = [...new Set(entries.map(e => e.action))];
  const uniqueResources = [...new Set(entries.map(e => e.resource_type))];

  const handleExport = () => {
    const headers = ['Zeitpunkt', 'Admin', 'Aktion', 'Ressource', 'Ressource-ID', 'Alt', 'Neu'];
    const rows = filtered.map(e => [
      new Date(e.created_at).toLocaleString('de-DE'),
      e.admin_email,
      e.action,
      e.resource_type,
      e.resource_id || '',
      JSON.stringify(e.old_value || {}),
      JSON.stringify(e.new_value || {}),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahryo_audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><FileText size={24} /> Audit Log</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><FileText size={24} /> Audit Log</h1>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={handleExport}>
            <Download size={14} /> CSV Export
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadAuditLog}>
            <RefreshCw size={14} /> Aktualisieren
          </button>
        </div>
      </div>

      <div className="grid-cols-3 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Einträge gesamt</span>
          <span className="kpi-value">{entries.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Heute</span>
          <span className="kpi-value">{entries.filter(e => new Date(e.created_at).toDateString() === new Date().toDateString()).length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Aktionstypen</span>
          <span className="kpi-value">{uniqueActions.length}</span>
        </div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <div className="flex gap-3 items-center" style={{ flex: 1, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', minWidth: 200 }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input className="input input-search" placeholder="Suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            <select className="input" style={{ width: 180 }} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
              <option value="">Alle Aktionen</option>
              {uniqueActions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
            </select>
            <select className="input" style={{ width: 140 }} value={filterResource} onChange={e => setFilterResource(e.target.value)}>
              <option value="">Alle Ressourcen</option>
              {uniqueResources.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <span className="text-xs text-muted">{filtered.length} Einträge</span>
        </div>

        <table>
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Admin</th>
              <th>Aktion</th>
              <th>Ressource</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  {entries.length === 0 ? 'Noch keine Audit-Einträge vorhanden.' : 'Keine Treffer für diese Filter.'}
                </td>
              </tr>
            ) : (
              filtered.map(entry => (
                <tr key={entry.id}>
                  <td className="text-xs" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(entry.created_at).toLocaleString('de-DE')}
                  </td>
                  <td className="text-sm">{entry.admin_email}</td>
                  <td>
                    <span className={`badge ${ACTION_COLORS[entry.action] || 'badge-neutral'}`}>
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                  </td>
                  <td>
                    <span className="text-sm">{entry.resource_type}</span>
                    {entry.resource_id && (
                      <span className="text-mono text-xs text-muted" style={{ marginLeft: 4 }}>{entry.resource_id}</span>
                    )}
                  </td>
                  <td className="text-xs text-muted">
                    {entry.old_value && entry.new_value && (
                      <span>
                        {JSON.stringify(entry.old_value)} &rarr; {JSON.stringify(entry.new_value)}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
